import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import getWindow from 'Utils/get-window';

/**
 * Collects this page view's Core Web Vitals and reports them as they settle.
 *
 * Why not a beacon per metric (which is what this module used to do): LCP, CLS
 * and INP are not knowable at load. LCP is only final once the user interacts
 * or the page is hidden, CLS accumulates for the whole visit, and INP needs
 * interactions to have happened. Batching at the moment they settle turns five
 * beacons per page view into roughly one, which matters when the caller is
 * sampling across ~12M daily views.
 *
 * Why not a SINGLE report at the first hide either: the first hidden transition
 * is not necessarily the end of the page view. A reader can switch tabs and
 * come back, and web-vitals keeps accumulating CLS and INP when they do. If we
 * reported once and tore down, every such visit would contribute only what it
 * had accrued before its first backgrounding, which biases the field data
 * optimistic in exactly the sessions that are longest and most engaged.
 *
 * So: report on every hidden transition where something actually changed, and
 * stamp each report with a page-view id and a sequence number.
 *
 *   Downstream, take the highest `pv_seq` per `pv_id` to get the final value
 *   for a page view. Do NOT sum or average across rows sharing a `pv_id`, and
 *   count page views by distinct `pv_id`, not by row.
 *
 * A back/forward-cache restore starts a genuinely new page view: web-vitals
 * resets and re-reports, so we rotate `pv_id` on restore rather than letting
 * the restored metrics look like a later update to the previous view.
 *
 * Metric availability is not guaranteed and absence is meaningful, not an
 * error: a page hidden before first paint has no LCP, and a page nobody clicked
 * has no INP. Missing keys are omitted rather than sent as zero, so the
 * percentiles downstream are computed over pages that actually have the metric.
 *
 * @param {Function} report - called once per hidden transition that changed something
 */
const collectVitals = (report) => {
  const w = getWindow();
  if (!w || typeof w.addEventListener !== 'function') {
    return;
  }

  const newPageViewId = () =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  let pvId = newPageViewId();
  let seq = 0;
  let metrics = {};
  let navigationType = null;
  let lastSent = null;

  const record = (metric) => {
    metrics[metric.name] = metric.value;
    if (metric.navigationType) {
      navigationType = metric.navigationType;
    }
  };

  onTTFB(record);
  onFCP(record);
  onLCP(record);
  onCLS(record);
  onINP(record);

  const snapshot = () => {
    const data = {};
    // Whole milliseconds: sub-millisecond precision is noise at these
    // magnitudes and it keeps the serialized event small.
    const ms = (key, name) => {
      if (typeof metrics[name] === 'number') {
        data[key] = Math.round(metrics[name]);
      }
    };
    ms('ttfb_ms', 'TTFB');
    ms('fcp_ms', 'FCP');
    ms('lcp_ms', 'LCP');
    ms('inp_ms', 'INP');
    // CLS is a unitless ratio in the low hundredths, so it is the one metric
    // that needs real decimals. Four is well past the 0.1 / 0.25 thresholds.
    if (typeof metrics.CLS === 'number') {
      data.cls = Math.round(metrics.CLS * 10000) / 10000;
    }
    if (navigationType) {
      data.nav_type = navigationType;
    }
    return data;
  };

  const flush = () => {
    const data = snapshot();
    // Nothing settled yet (e.g. the tab was hidden immediately). Sending an
    // empty report would just add a row that drags the percentiles around.
    if (Object.keys(data).length === 0) {
      return;
    }
    // Rounded values are what we send, so compare the rounded form: a CLS that
    // moved in the sixth decimal is not worth a second event.
    const fingerprint = JSON.stringify(data);
    if (fingerprint === lastSent) {
      return;
    }
    lastSent = fingerprint;
    seq += 1;
    report({ ...data, pv_id: pvId, pv_seq: seq });
  };

  // 'visibilitychange' also fires on the way back to visible; only the hidden
  // transition is a moment where the metrics so far are worth banking.
  const onVisibilityChange = () => {
    if (w.document && w.document.visibilityState === 'hidden') {
      flush();
    }
  };

  // 'pagehide' gets no visibility guard: some browsers still report
  // visibilityState 'visible' during it, and it is the hook that fires on a
  // same-tab navigation away.
  const onPageHide = () => flush();

  // A bfcache restore is a new page view for Core Web Vitals purposes, and
  // web-vitals resets and re-reports accordingly. Rotate the id so those
  // metrics are not mistaken for a later update to the previous view.
  const onPageShow = (event) => {
    if (event && event.persisted) {
      pvId = newPageViewId();
      seq = 0;
      metrics = {};
      navigationType = null;
      lastSent = null;
    }
  };

  // Capture phase, so a listener downstream that stops propagation cannot
  // silently cost us the report. Registered AFTER the onX() calls above, so
  // that when the page hides the web-vitals listeners (registered first) have
  // already pushed their current values into `metrics` by the time these run:
  // listener order within an event is registration order, which is what makes
  // reading `metrics` here safe.
  w.addEventListener('visibilitychange', onVisibilityChange, true);
  w.addEventListener('pagehide', onPageHide, true);
  w.addEventListener('pageshow', onPageShow, true);
};

export default collectVitals;
